import React, { useEffect, useRef } from "react";

import { stepPlayer } from "../server/physics";
import { SoloSim } from "./solo-sim";
import {
	animFrame,
	BG_HILL,
	BG_SKY,
	EAGLE,
	GROUND_PROPS,
	ITEM_CHERRY,
	ITEM_GEM,
	LANDMARK_PROPS,
	OPOSSUM,
	PLAYER,
	POWERUP_PULSE,
	TILE_GROUND,
	TILE_PLATFORM,
} from "./image-assets";

import { ENEMY_HEIGHT, ENEMY_WIDTH } from "../level";

import type { AnimSet } from "./image-assets";
import type { LandmarkKind, LevelConfig } from "../level";
import type { GameInput, PlayerTickState, ServerMessage } from "../shared";
import type { PlayerPhysicsState } from "../server/physics";

// Base (unscaled) footprint, in world px, used both for sprite kinds and the
// generic silhouette fallback below.
const LANDMARK_BASE_SIZE: Record<LandmarkKind, { width: number; height: number }> = {
	hill: { width: 90, height: 60 },
	lighthouse: { width: 28, height: 64 },
	temple: { width: 40, height: 56 },
	submarine: { width: 80, height: 32 },
	statue: { width: 30, height: 50 },
	dome: { width: 48, height: 40 },
	pier: { width: 48, height: 36 },
	caves: { width: 60, height: 34 },
	ferris: { width: 56, height: 48 },
	bridge: { width: 100, height: 26 },
	aircraft: { width: 70, height: 24 },
	port: { width: 40, height: 40 },
};

const VIEW_WIDTH = 800;
const VIEW_HEIGHT = 480;

// Matches the server's TICK_MS (game-session.ts) — used to time remote-entity
// interpolation and to judge how stale a prediction/server disagreement is.
const SERVER_TICK_MS = 50;
// Above this, trust the server outright (respawn/teleport) instead of easing
// toward it — a small physics gap gets smoothed, a large one gets snapped.
const RECONCILE_SNAP_PX = 40;

const COLLECTIBLE_SIZE = 20;

// SunnyLand ships two collectible sprites (gem, cherry); the level generator
// still cycles three logical collectible types (punugulu/corn/coconut), so
// two of them share the gem art.
const COLLECTIBLE_SPRITE: Record<string, AnimSet> = {
	punugulu: ITEM_GEM,
	corn: ITEM_CHERRY,
	coconut: ITEM_GEM,
};

// Foxy's frames are a native 33x32 — drawn a bit larger than the PLAYER_WIDTH
// x PLAYER_HEIGHT (24x32) hitbox and centered/foot-anchored onto it so the
// hitbox stays the authoritative collision box while the art reads at a
// natural size.
const PLAYER_DRAW_WIDTH = 40;
const PLAYER_DRAW_HEIGHT = (PLAYER_DRAW_WIDTH * PLAYER.IDLE.nativeHeight) / PLAYER.IDLE.nativeWidth;
const OPOSSUM_DRAW_WIDTH = 34;
const OPOSSUM_DRAW_HEIGHT = (OPOSSUM_DRAW_WIDTH * OPOSSUM.nativeHeight) / OPOSSUM.nativeWidth;
const EAGLE_DRAW_WIDTH = 34;
const EAGLE_DRAW_HEIGHT = (EAGLE_DRAW_WIDTH * EAGLE.nativeHeight) / EAGLE.nativeWidth;
const GROUND_TILE_PX = 2; // 16x16 native tile -> 32x32 world px

// Draws `img` scaled to (width, height) and anchored by its bottom-center
// point at world position (footX, footY) — the common case for
// characters/enemies whose hitbox bottom should sit exactly at the art's feet.
function drawFootAnchored(
	ctx: CanvasRenderingContext2D,
	img: CanvasImageSource,
	footX: number,
	footY: number,
	width: number,
	height: number,
) {
	ctx.drawImage(img, footX - width / 2, footY - height, width, height);
}

// Draws `img` stretched to exactly fill the world-space box (x, y, width,
// height) — used for tiles/collectibles/power-ups where the hitbox itself is
// the box.
function drawBoxed(ctx: CanvasRenderingContext2D, img: CanvasImageSource, x: number, y: number, width: number, height: number) {
	ctx.drawImage(img, x, y, width, height);
}

type TickMessage = Extract<ServerMessage, { type: "tick" }>;
type Snapshot = Omit<TickMessage, "type">;

function keyToInputField(key: string): keyof GameInput | null {
	if (key === "ArrowLeft" || key === "a" || key === "A") return "left";
	if (key === "ArrowRight" || key === "d" || key === "D") return "right";
	if (key === "ArrowUp" || key === "w" || key === "W" || key === " ") return "jump";
	return null;
}

export function GameView({
	socket,
	localPlayerId,
	level,
	soloSim,
	paused,
	onTick,
}: {
	socket: WebSocket | null;
	localPlayerId: string;
	level: LevelConfig;
	soloSim?: SoloSim;
	paused?: boolean;
	onTick?: (tick: Snapshot) => void;
}) {
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const tickRef = useRef<Snapshot | null>(null);
	const prevTickRef = useRef<Snapshot | null>(null);
	const tickReceivedAtRef = useRef<number>(0);
	const inputRef = useRef<GameInput>({ left: false, right: false, jump: false });
	// Locally-simulated local-player state, advanced every animation frame from
	// held input so the local player responds instantly instead of waiting for
	// a server round trip; corrected/reconciled against each server tick below.
	// In solo mode this *is* the authoritative state (SoloSim runs the same
	// rules locally), so there's nothing to reconcile.
	const predictedRef = useRef<PlayerPhysicsState | null>(null);
	const facingRef = useRef<"left" | "right">("right");

	useEffect(() => {
		// Best-effort: on touch devices, lock to landscape so the d-pad has
		// somewhere to live. Both APIs require a fullscreen/user-gesture context
		// in most browsers and are unsupported in several (notably iOS Safari),
		// so failures are silently ignored — the CSS rotate-prompt is the
		// fallback that always works.
		if (!window.matchMedia("(pointer: coarse)").matches) return;
		const orientation = screen.orientation as (ScreenOrientation & { lock?: (o: string) => Promise<void> }) | undefined;
		orientation?.lock?.("landscape").catch(() => {});
		return () => {
			(screen.orientation as (ScreenOrientation & { unlock?: () => void }) | undefined)?.unlock?.();
		};
	}, []);

	useEffect(() => {
		if (!socket) return;
		function handleMessage(event: MessageEvent) {
			const message = JSON.parse(event.data as string) as ServerMessage;
			if (message.type !== "tick") return;
			prevTickRef.current = tickRef.current;
			tickRef.current = message;
			tickReceivedAtRef.current = Date.now();
			onTick?.(message);

			const server = message.players.find((p) => p.playerId === localPlayerId);
			if (!server) return;
			facingRef.current = server.facing;
			const pred = predictedRef.current;
			if (!pred) {
				predictedRef.current = { x: server.x, y: server.y, vx: 0, vy: 0, onGround: true };
				return;
			}
			const dist = Math.hypot(pred.x - server.x, pred.y - server.y);
			if (dist > RECONCILE_SNAP_PX) {
				// Large gap: respawn, checkpoint reset, stomp bounce, etc. — trust
				// the server outright rather than easing into it.
				predictedRef.current = { x: server.x, y: server.y, vx: 0, vy: 0, onGround: true };
			} else if (dist > 0.5) {
				// Small gap: gravity-integration drift from client running finer
				// timesteps than the server's fixed 20Hz — ease toward the
				// authoritative position instead of snapping (avoids visible pop).
				pred.x += (server.x - pred.x) * 0.2;
				pred.y += (server.y - pred.y) * 0.2;
			}
		}
		socket.addEventListener("message", handleMessage);
		return () => socket.removeEventListener("message", handleMessage);
	}, [socket, localPlayerId, onTick]);

	// Shared by keyboard and on-screen touch d-pad — both just flip a field and
	// flush the current input state to the server (or the local predicted step
	// picks it up on the next animation frame in solo mode).
	function setInputField(field: keyof GameInput, value: boolean) {
		if (inputRef.current[field] === value) return;
		inputRef.current = { ...inputRef.current, [field]: value };
		if (socket && socket.readyState === WebSocket.OPEN) {
			socket.send(JSON.stringify({ type: "input", ...inputRef.current }));
		}
	}

	useEffect(() => {
		function onKeyDown(event: KeyboardEvent) {
			const field = keyToInputField(event.key);
			if (!field) return;
			event.preventDefault();
			setInputField(field, true);
		}
		function onKeyUp(event: KeyboardEvent) {
			const field = keyToInputField(event.key);
			if (!field) return;
			setInputField(field, false);
		}
		window.addEventListener("keydown", onKeyDown);
		window.addEventListener("keyup", onKeyUp);
		return () => {
			window.removeEventListener("keydown", onKeyDown);
			window.removeEventListener("keyup", onKeyUp);
		};
	}, [socket]);

	useEffect(() => {
		const canvas = canvasRef.current;
		const ctx = canvas?.getContext("2d");
		if (!canvas || !ctx) return;
		let raf = 0;
		let lastFrameTs = performance.now();
		let lastReportMs = 0;

		function draw() {
			raf = requestAnimationFrame(draw);

			const frameNow = performance.now();
			const dtSec = Math.min((frameNow - lastFrameTs) / 1000, 0.1);
			lastFrameTs = frameNow;

			if (soloSim) {
				// No network involved: SoloSim IS the authoritative simulation, run
				// right here at display rate instead of a fixed 20Hz server tick, so
				// there's no reconciliation and no interpolation lag to smooth over.
				if (!paused) {
					const snap = soloSim.step(inputRef.current, dtSec);
					tickRef.current = snap;
					prevTickRef.current = snap;
					tickReceivedAtRef.current = Date.now();
					const me = snap.players[0];
					predictedRef.current = { x: me.x, y: me.y, vx: 0, vy: 0, onGround: true };
					facingRef.current = me.facing;
					const now = performance.now();
					if (onTick && (now - lastReportMs > 100 || me.finished || me.gameOver)) {
						lastReportMs = now;
						onTick(snap);
					}
				}
			} else if (predictedRef.current) {
				const next = stepPlayer(
					predictedRef.current,
					inputRef.current,
					level.platforms,
					level.groundY,
					level.width,
					dtSec,
					level.groundGaps,
				);
				predictedRef.current = next;
				if (next.vx > 0) facingRef.current = "right";
				else if (next.vx < 0) facingRef.current = "left";
			}

			const tick = tickRef.current;
			const prevTick = prevTickRef.current;

			// How far we are into the gap between the last two server ticks —
			// used to smoothly interpolate remote entities (enemies, other
			// players) instead of visibly snapping every 50ms. Always 1 for solo
			// (prevTick === tick every frame), so interp() below is a no-op.
			const tickAlpha = Math.min(1, Math.max(0, (Date.now() - tickReceivedAtRef.current) / SERVER_TICK_MS));
			function interp(prev: { x: number; y: number } | undefined, cur: { x: number; y: number }) {
				if (!prev) return cur;
				return { x: prev.x + (cur.x - prev.x) * tickAlpha, y: prev.y + (cur.y - prev.y) * tickAlpha };
			}

			ctx!.clearRect(0, 0, VIEW_WIDTH, VIEW_HEIGHT);

			const localPos = predictedRef.current ?? tick?.players.find((p) => p.playerId === localPlayerId);
			const cameraX = localPos
				? Math.max(0, Math.min(level.width - VIEW_WIDTH, localPos.x - VIEW_WIDTH / 2))
				: 0;
			const toScreenX = (worldX: number) => worldX - cameraX;

			const now = Date.now();

			// Wraps `value` into [0, modulus) — plain `%` returns a negative result
			// for a negative `value` in JS, which is what made the old tiling-start
			// formula below blow up for large cameraX (it could land the first
			// tile thousands of px past VIEW_WIDTH, so the fill loop never ran and
			// the layer just vanished once the camera had scrolled far enough).
			function wrap(value: number, modulus: number): number {
				return ((value % modulus) + modulus) % modulus;
			}

			// Sky — SunnyLand's sky+cloud+ocean band, tiled horizontally at a slow
			// parallax factor so it never visibly repeats-snaps on the long levels.
			const skyDrawH = VIEW_HEIGHT;
			const skyDrawW = (BG_SKY.naturalWidth || 384) * (skyDrawH / (BG_SKY.naturalHeight || 240));
			if (BG_SKY.complete && skyDrawW > 0) {
				const skyStart = -wrap(cameraX * 0.15, skyDrawW);
				for (let sx = skyStart; sx < VIEW_WIDTH; sx += skyDrawW) {
					ctx!.drawImage(BG_SKY, sx, 0, skyDrawW, skyDrawH);
				}
			} else {
				ctx!.fillStyle = level.theme.skyBottom;
				ctx!.fillRect(0, 0, VIEW_WIDTH, VIEW_HEIGHT);
			}

			// Far parallax layer — distant jungle-hill silhouette, scrolls slower
			// than the sky so the much longer levels still read as terrain passing by.
			const hillDrawH = 220;
			const hillDrawW = (BG_HILL.naturalWidth || 176) * (hillDrawH / (BG_HILL.naturalHeight || 368));
			if (BG_HILL.complete && hillDrawW > 0) {
				const hillY = level.groundY - hillDrawH + 40;
				const hillStart = -wrap(cameraX * 0.35, hillDrawW);
				for (let hx = hillStart; hx < VIEW_WIDTH; hx += hillDrawW) {
					ctx!.drawImage(BG_HILL, hx, hillY, hillDrawW, hillDrawH);
				}
			}

			// Landmark set-pieces (background, behind the player/enemies/ground) —
			// each level supplies its own list (level.landmarks); every kind maps to
			// a SunnyLand prop (see LANDMARK_PROPS). Each gets a name tag underneath,
			// per level.ts's contract.
			for (const landmark of level.landmarks) {
				const base = LANDMARK_BASE_SIZE[landmark.kind];
				const scale = landmark.scale ?? 1;
				const width = base.width * scale;
				const height = base.height * scale;
				const screenX = toScreenX(landmark.x) - width / 2;
				const y = level.groundY - height;
				if (screenX + width < 0 || screenX > VIEW_WIDTH) continue;

				const prop = LANDMARK_PROPS[landmark.kind];
				if (prop?.complete) {
					drawBoxed(ctx!, prop, screenX, y, width, height);
				} else {
					ctx!.fillStyle = "rgba(20, 20, 30, 0.55)";
					ctx!.beginPath();
					ctx!.ellipse(screenX + width / 2, y + height, width / 2, height, 0, Math.PI, 0);
					ctx!.fill();
				}

				ctx!.fillStyle = "rgba(255, 255, 255, 0.85)";
				ctx!.font = "10px sans-serif";
				ctx!.textAlign = "center";
				ctx!.fillText(landmark.name, screenX + width / 2, level.groundY + 14);
			}

			// Decorative ground-level props (palm/pine/tree/bush/rock) — picked
			// deterministically per world-x slot so they don't jitter frame to
			// frame, purely cosmetic (no collision).
			const propStart = Math.floor(cameraX / 320) * 320;
			for (let gx = propStart; gx < cameraX + VIEW_WIDTH + 320; gx += 320) {
				const prop = GROUND_PROPS[Math.floor(gx / 320) % GROUND_PROPS.length];
				if (!prop.complete) continue;
				const h = 70;
				const w = (prop.naturalWidth / prop.naturalHeight) * h;
				drawBoxed(ctx!, prop, toScreenX(gx), level.groundY - h, w, h);
			}

			// Ground gaps — a dark pit with a warning edge, drawn where the ground
			// strip is deliberately missing (see level.groundGaps / physics.ts).
			for (const gap of level.groundGaps) {
				if (gap.x + gap.width < cameraX || gap.x > cameraX + VIEW_WIDTH) continue;
				const gradient = ctx!.createLinearGradient(0, level.groundY, 0, VIEW_HEIGHT);
				gradient.addColorStop(0, "#1a0f0a");
				gradient.addColorStop(1, "#000000");
				ctx!.fillStyle = gradient;
				ctx!.fillRect(toScreenX(gap.x), level.groundY, gap.width, VIEW_HEIGHT - level.groundY);
				ctx!.fillStyle = "#e0483e";
				ctx!.fillRect(toScreenX(gap.x), level.groundY, 3, 10);
				ctx!.fillRect(toScreenX(gap.x + gap.width) - 3, level.groundY, 3, 10);
			}

			// Ground — tiled across the visible viewport, skipping any tile that
			// falls inside a gap so the pit drawn above stays uncovered.
			const groundTileStart = Math.floor(cameraX / (16 * GROUND_TILE_PX)) * (16 * GROUND_TILE_PX);
			for (let gx = groundTileStart; gx < cameraX + VIEW_WIDTH; gx += 16 * GROUND_TILE_PX) {
				const tileW = 16 * GROUND_TILE_PX;
				const inGap = level.groundGaps.some((gap) => gx < gap.x + gap.width && gx + tileW > gap.x);
				if (inGap) continue;
				for (let gy = level.groundY; gy < VIEW_HEIGHT; gy += 16 * GROUND_TILE_PX) {
					drawBoxed(ctx!, TILE_GROUND, toScreenX(gx), gy, 16 * GROUND_TILE_PX, 16 * GROUND_TILE_PX);
				}
			}

			// Platforms — tiled to each platform's exact box
			for (const platform of level.platforms) {
				const tilePx = platform.height / 16;
				const tileW = 16 * tilePx;
				for (let px2 = 0; px2 < platform.width; px2 += tileW) {
					const w = Math.min(tileW, platform.width - px2);
					drawBoxed(ctx!, TILE_PLATFORM, toScreenX(platform.x + px2), platform.y, w, platform.height);
				}
			}

			// Checkpoint marker
			ctx!.fillStyle = "#4caf50";
			ctx!.fillRect(toScreenX(level.checkpointX) - 2, level.groundY - 80, 4, 80);
			ctx!.fillRect(toScreenX(level.checkpointX) - 2, level.groundY - 80, 24, 14);

			// Collectibles not yet picked up
			const collected = new Set(tick?.collectedIds ?? []);
			for (const collectible of level.collectibles) {
				if (collected.has(collectible.id)) continue;
				const sprite = COLLECTIBLE_SPRITE[collectible.type];
				if (!sprite) continue;
				const frame = animFrame(sprite, now, 120);
				if (!frame.complete) continue;
				drawBoxed(ctx!, frame, toScreenX(collectible.x), collectible.y, COLLECTIBLE_SIZE, COLLECTIBLE_SIZE);
			}

			// Power-up
			if (!tick?.powerUpTaken) {
				const frame = animFrame(POWERUP_PULSE, now, 100);
				if (frame.complete) {
					drawBoxed(ctx!, frame, toScreenX(level.powerUp.x), level.powerUp.y, 24, 24);
				}
			}

			// Enemies — Opossum patrols the ground (old "crab" ids), Eagle swoops in
			// the air (old "seagull" ids); both id namespaces are unchanged from
			// level.ts so no data migration is needed here.
			for (const enemy of tick?.enemies ?? []) {
				if (!enemy.alive) continue;
				const isGround = enemy.id.startsWith("crab");
				const anim = isGround ? OPOSSUM : EAGLE;
				const drawW = isGround ? OPOSSUM_DRAW_WIDTH : EAGLE_DRAW_WIDTH;
				const drawH = isGround ? OPOSSUM_DRAW_HEIGHT : EAGLE_DRAW_HEIGHT;
				const frame = animFrame(anim, now, 150);
				if (!frame.complete) continue;
				const prevEnemy = prevTick?.enemies.find((e) => e.id === enemy.id);
				const pos = interp(prevEnemy, enemy);
				drawFootAnchored(ctx!, frame, toScreenX(pos.x) + ENEMY_WIDTH / 2, pos.y + ENEMY_HEIGHT, drawW, drawH);
			}

			// Players
			(tick?.players ?? []).forEach((player: PlayerTickState) => {
				const isLocal = player.playerId === localPlayerId;
				const pos =
					isLocal && predictedRef.current
						? predictedRef.current
						: interp(prevTick?.players.find((p) => p.playerId === player.playerId), player);
				const facing = isLocal ? facingRef.current : player.facing;
				const local = isLocal ? predictedRef.current : null;
				const anim = !local || local.onGround === false ? PLAYER.JUMP : local.vx === 0 ? PLAYER.IDLE : PLAYER.RUN;
				const frame = animFrame(anim, now, 110);
				if (!frame.complete) return;
				const footX = toScreenX(pos.x) + 12; // PLAYER_WIDTH / 2
				const footY = pos.y + 32; // PLAYER_HEIGHT
				ctx!.save();
				if (player.invincible) ctx!.globalAlpha = Math.floor(now / 100) % 2 === 0 ? 0.5 : 1;
				if (facing === "left") {
					ctx!.translate(footX, 0);
					ctx!.scale(-1, 1);
					drawFootAnchored(ctx!, frame, 0, footY, PLAYER_DRAW_WIDTH, PLAYER_DRAW_HEIGHT);
				} else {
					drawFootAnchored(ctx!, frame, footX, footY, PLAYER_DRAW_WIDTH, PLAYER_DRAW_HEIGHT);
				}
				ctx!.restore();
				if (isLocal) {
					ctx!.strokeStyle = "#fff";
					ctx!.lineWidth = 2;
					ctx!.strokeRect(toScreenX(pos.x), pos.y, 24, 32);
				}
			});

			// Per-level mood wash — drawn last, over the whole scene (level.theme).
			if (level.theme.tintAlpha > 0) {
				ctx!.fillStyle = level.theme.tint;
				ctx!.globalAlpha = level.theme.tintAlpha;
				ctx!.fillRect(0, 0, VIEW_WIDTH, VIEW_HEIGHT);
				ctx!.globalAlpha = 1;
			}
		}

		raf = requestAnimationFrame(draw);
		return () => cancelAnimationFrame(raf);
	}, [localPlayerId, level, soloSim, paused, onTick]);

	// Touch buttons hold a field down for as long as the finger is on it —
	// pointer events (not click) so multiple buttons can be held at once and
	// leaving/cancelling the touch (finger slides off) releases it like a key.
	function touchHandlers(field: keyof GameInput) {
		return {
			onPointerDown: (event: React.PointerEvent) => {
				event.preventDefault();
				(event.target as Element).setPointerCapture?.(event.pointerId);
				setInputField(field, true);
			},
			onPointerUp: (event: React.PointerEvent) => {
				event.preventDefault();
				setInputField(field, false);
			},
			onPointerCancel: () => setInputField(field, false),
			onPointerLeave: () => setInputField(field, false),
			onContextMenu: (event: React.SyntheticEvent) => event.preventDefault(),
		};
	}

	return (
		<div className="game-stage">
			<canvas ref={canvasRef} width={VIEW_WIDTH} height={VIEW_HEIGHT} className="game-canvas" />
			<div className="rotate-device-overlay">
				<div className="rotate-device-icon" aria-hidden="true">
					⟳
				</div>
				<p>Rotate your device to landscape to play</p>
			</div>
			<div className="touch-controls" aria-hidden="true">
				<div className="touch-dpad">
					<button type="button" className="touch-btn touch-btn-left" {...touchHandlers("left")}>
						◀
					</button>
					<button type="button" className="touch-btn touch-btn-right" {...touchHandlers("right")}>
						▶
					</button>
				</div>
				<button type="button" className="touch-btn touch-btn-jump" {...touchHandlers("jump")}>
					JUMP
				</button>
			</div>
		</div>
	);
}
